import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, Users, Package, Calendar, DollarSign,
  Download, Filter, RotateCcw, BarChart3, PieChart as PieChartIcon,
  TrendingUp, Clock, Bed
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#C4A77D', '#8B7355', '#A0522D', '#6B4423', '#D4AF37', '#B8860B'];

const EnhancedReports = ({ user, onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('room-utilization');
  
  // Filters
  const [filterProperty, setFilterProperty] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Report data
  const [roomUtilization, setRoomUtilization] = useState(null);
  const [leaveTracking, setLeaveTracking] = useState(null);
  const [packageStatus, setPackageStatus] = useState(null);
  const [therapistPerformance, setTherapistPerformance] = useState(null);
  const [revenueByProperty, setRevenueByProperty] = useState(null);

  useEffect(() => {
    fetchProperties();
    // Set default date range (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(lastDay.toISOString().split('T')[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchReports();
    }
  }, [filterProperty, dateFrom, dateTo, activeTab]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.filter(p => p.status !== 'archived'));
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const params = new URLSearchParams();
    if (filterProperty && filterProperty !== 'all') params.append('property_id', filterProperty);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    
    try {
      const [roomRes, leaveRes, packageRes, perfRes, revenueRes] = await Promise.all([
        axios.get(`${API}/reports/room-utilization?${params.toString()}`, { headers }),
        axios.get(`${API}/reports/leave-tracking?${params.toString()}`, { headers }),
        axios.get(`${API}/reports/package-status?${params.toString()}`, { headers }),
        axios.get(`${API}/reports/therapist-performance?${params.toString()}`, { headers }),
        axios.get(`${API}/reports/revenue-by-property?${params.toString()}`, { headers })
      ]);
      
      setRoomUtilization(roomRes.data);
      setLeaveTracking(leaveRes.data);
      setPackageStatus(packageRes.data);
      setTherapistPerformance(perfRes.data);
      setRevenueByProperty(revenueRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterProperty('all');
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(lastDay.toISOString().split('T')[0]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream">
      {/* Header */}
      <div className="glass border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">Enhanced Reports</h1>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout}>Logout</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <div className="glass rounded-2xl p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="w-[180px]" data-testid="filter-property">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[150px]"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[150px]"
            />
            
            <Button variant="outline" size="icon" onClick={resetFilters} title="Reset">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full bg-muted/50">
            <TabsTrigger value="room-utilization" className="flex items-center gap-2">
              <Bed className="w-4 h-4" /> Rooms
            </TabsTrigger>
            <TabsTrigger value="leave-tracking" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Leaves
            </TabsTrigger>
            <TabsTrigger value="package-status" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Packages
            </TabsTrigger>
            <TabsTrigger value="therapist-performance" className="flex items-center gap-2">
              <Users className="w-4 h-4" /> Therapists
            </TabsTrigger>
            <TabsTrigger value="revenue-by-property" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Revenue
            </TabsTrigger>
          </TabsList>

          {/* Room Utilization */}
          <TabsContent value="room-utilization" className="space-y-4">
            {roomUtilization && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Rooms</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{roomUtilization.total_rooms}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Bookings</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{roomUtilization.total_bookings}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Avg Bookings/Room</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {roomUtilization.total_rooms > 0 
                          ? (roomUtilization.total_bookings / roomUtilization.total_rooms).toFixed(1)
                          : 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-serif mb-4">Room Utilization</h3>
                  {roomUtilization.room_utilization.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={roomUtilization.room_utilization}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="room_name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_bookings" fill="#C4A77D" name="Bookings" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No data available</p>
                  )}
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-serif mb-4">Room Details</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Room</th>
                        <th className="text-left py-2">Property</th>
                        <th className="text-right py-2">Bookings</th>
                        <th className="text-right py-2">Hours Used</th>
                        <th className="text-right py-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomUtilization.room_utilization.map(room => (
                        <tr key={room.room_id} className="border-b border-border/30">
                          <td className="py-2 font-medium">{room.room_name}</td>
                          <td className="py-2 text-sm text-muted-foreground">{room.property_id}</td>
                          <td className="py-2 text-right">{room.total_bookings}</td>
                          <td className="py-2 text-right">{room.total_hours.toFixed(1)} hrs</td>
                          <td className="py-2 text-right">{formatCurrency(room.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* Leave Tracking */}
          <TabsContent value="leave-tracking" className="space-y-4">
            {leaveTracking && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Leaves</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{leaveTracking.total_leaves}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-green-200">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-green-600">Approved</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">{leaveTracking.total_approved}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-red-200">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-red-600">Rejected</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">{leaveTracking.total_rejected}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-amber-200">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-amber-600">Pending</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-amber-600">{leaveTracking.total_pending}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-serif mb-4">Leave by Therapist</h3>
                  {leaveTracking.by_therapist.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Therapist</th>
                          <th className="text-left py-2">Property</th>
                          <th className="text-right py-2">Total Leaves</th>
                          <th className="text-right py-2">Days</th>
                          <th className="text-right py-2">Approved</th>
                          <th className="text-right py-2">Rejected</th>
                          <th className="text-right py-2">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveTracking.by_therapist.map(t => (
                          <tr key={t.therapist_id} className="border-b border-border/30">
                            <td className="py-2 font-medium">{t.therapist_name}</td>
                            <td className="py-2 text-sm text-muted-foreground">{t.property_id}</td>
                            <td className="py-2 text-right">{t.total_leaves}</td>
                            <td className="py-2 text-right">{t.total_days}</td>
                            <td className="py-2 text-right text-green-600">{t.approved}</td>
                            <td className="py-2 text-right text-red-600">{t.rejected}</td>
                            <td className="py-2 text-right text-amber-600">{t.pending}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No leave data found</p>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Package Status */}
          <TabsContent value="package-status" className="space-y-4">
            {packageStatus && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Packages</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{packageStatus.total_packages}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(packageStatus.total_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-green-200">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-green-600">Active</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">{packageStatus.active_packages}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(packageStatus.active_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Completed</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{packageStatus.completed_packages}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass border-red-200">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-red-600">Expired</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">{packageStatus.expired_packages}</p>
                    </CardContent>
                  </Card>
                </div>

                {packageStatus.packages.active.length > 0 && (
                  <div className="glass rounded-2xl p-6">
                    <h3 className="text-lg font-serif mb-4">Active Packages</h3>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Guest</th>
                          <th className="text-left py-2">Package</th>
                          <th className="text-left py-2">Property</th>
                          <th className="text-right py-2">Sessions</th>
                          <th className="text-right py-2">Value</th>
                          <th className="text-left py-2">Valid Until</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packageStatus.packages.active.map(pkg => (
                          <tr key={pkg.id} className="border-b border-border/30">
                            <td className="py-2 font-medium">{pkg.guest_name}</td>
                            <td className="py-2">{pkg.package_name}</td>
                            <td className="py-2 text-sm text-muted-foreground">{pkg.property_id}</td>
                            <td className="py-2 text-right">
                              {pkg.sessions_remaining} / {pkg.total_sessions}
                            </td>
                            <td className="py-2 text-right">{formatCurrency(pkg.total_price)}</td>
                            <td className="py-2">{pkg.valid_until}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Therapist Performance */}
          <TabsContent value="therapist-performance" className="space-y-4">
            {therapistPerformance && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Therapists</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{therapistPerformance.total_therapists}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Services</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{therapistPerformance.total_services}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Revenue</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{formatCurrency(therapistPerformance.total_revenue)}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-serif mb-4">Therapist Performance</h3>
                  {therapistPerformance.performance.length > 0 ? (
                    <>
                      <div className="h-[300px] mb-6">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={therapistPerformance.performance.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="therapist_name" angle={-45} textAnchor="end" height={80} />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Bar dataKey="total_revenue" fill="#C4A77D" name="Revenue" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Rank</th>
                            <th className="text-left py-2">Therapist</th>
                            <th className="text-left py-2">Property</th>
                            <th className="text-right py-2">Services</th>
                            <th className="text-right py-2">Revenue</th>
                            <th className="text-right py-2">Avg/Service</th>
                            <th className="text-right py-2">Rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {therapistPerformance.performance.map((t, index) => (
                            <tr key={t.therapist_id} className="border-b border-border/30">
                              <td className="py-2">
                                <Badge variant={index < 3 ? 'default' : 'outline'}>
                                  #{index + 1}
                                </Badge>
                              </td>
                              <td className="py-2 font-medium">{t.therapist_name}</td>
                              <td className="py-2 text-sm text-muted-foreground">{t.property_id}</td>
                              <td className="py-2 text-right">{t.total_services}</td>
                              <td className="py-2 text-right font-medium">{formatCurrency(t.total_revenue)}</td>
                              <td className="py-2 text-right">{formatCurrency(t.avg_service_value)}</td>
                              <td className="py-2 text-right">
                                {t.avg_rating > 0 ? `${t.avg_rating} ⭐` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No performance data</p>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Revenue by Property */}
          <TabsContent value="revenue-by-property" className="space-y-4">
            {revenueByProperty && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Properties</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{revenueByProperty.total_properties}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardDescription>Total Revenue</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{formatCurrency(revenueByProperty.total_revenue)}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="glass rounded-2xl p-6">
                    <h3 className="text-lg font-serif mb-4">Revenue Distribution</h3>
                    {revenueByProperty.by_property.length > 0 ? (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={revenueByProperty.by_property}
                              dataKey="total_revenue"
                              nameKey="property_name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ property_name, percent }) => `${property_name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {revenueByProperty.by_property.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-muted-foreground">No data</p>
                    )}
                  </div>

                  <div className="glass rounded-2xl p-6">
                    <h3 className="text-lg font-serif mb-4">Property Details</h3>
                    <div className="space-y-3">
                      {revenueByProperty.by_property.map((p, index) => (
                        <div key={p.property_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded" 
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <div>
                              <p className="font-medium">{p.property_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {p.city} • {p.total_services} services
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{formatCurrency(p.total_revenue)}</p>
                            <Badge variant={p.ownership_type === 'our_property' ? 'default' : p.ownership_type === 'owned_with_rent' ? 'secondary' : 'outline'} className="text-xs">
                              {p.ownership_type === 'our_property' ? 'Fully Owned' : p.ownership_type === 'owned_with_rent' ? 'Owned (Rent)' : 'Partner'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-serif mb-4">Detailed Breakdown</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Property</th>
                        <th className="text-left py-2">City</th>
                        <th className="text-left py-2">Type</th>
                        <th className="text-right py-2">Services</th>
                        <th className="text-right py-2">Revenue</th>
                        <th className="text-right py-2">GST</th>
                        <th className="text-right py-2">Company Share</th>
                        <th className="text-right py-2">To Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueByProperty.by_property.map(p => (
                        <tr key={p.property_id} className="border-b border-border/30">
                          <td className="py-2 font-medium">{p.property_name}</td>
                          <td className="py-2">{p.city}</td>
                          <td className="py-2">
                            <Badge variant={p.ownership_type === 'our_property' ? 'default' : p.ownership_type === 'owned_with_rent' ? 'secondary' : 'outline'}>
                              {p.ownership_type === 'our_property' ? 'Fully Owned' : p.ownership_type === 'owned_with_rent' ? 'Owned (Rent)' : 'Partner'}
                            </Badge>
                          </td>
                          <td className="py-2 text-right">{p.total_services}</td>
                          <td className="py-2 text-right font-medium">{formatCurrency(p.total_revenue)}</td>
                          <td className="py-2 text-right">{formatCurrency(p.gst_collected)}</td>
                          <td className="py-2 text-right text-green-600">{formatCurrency(p.company_share || p.nirvaana_share)}</td>
                          <td className="py-2 text-right text-amber-600">
                            {p.ownership_type === 'our_property' ? '₹0' : 
                             p.ownership_type === 'owned_with_rent' ? formatCurrency(p.fixed_rental_amount || 0) + ' (Rent)' :
                             formatCurrency(p.hotel_share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedReports;
